/**
 * Configuration Zod Schemas
 *
 * Single source of truth for configuration-related types. These Zod schemas generate:
 * 1. TypeScript types (via z.infer)
 * 2. OpenAPI schemas (via @asteasolutions/zod-to-openapi)
 *
 * @see lib/configuration/types.ts - Uses types derived from these schemas
 * @see lib/swagger/registry.ts - Registers these schemas for OpenAPI generation
 */

import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

// Extend Zod with OpenAPI methods
extendZodWithOpenApi(z);

// =============================================================================
// Locale Schemas
// =============================================================================

/**
 * Locale configuration for a language variant
 */
export const LocaleSchema = z
  .object({
    locale: z.string().openapi({
      description: 'Internal locale code',
      example: 'en-US',
    }),
    code: z.string().openapi({
      description: 'Algolia locale code',
      example: 'EN_US',
    }),
    name: z.string().openapi({
      description: 'Display name',
      example: 'English',
    }),
    slug: z.string().openapi({
      description: 'URL slug',
      example: 'en',
    }),
    sfCode: z.string().openapi({
      description: 'Salesforce language code',
    }),
    contentful: z.string().openapi({
      description: 'Contentful locale code',
    }),
  })
  .openapi('Locale');

/**
 * Country configuration for routing and locale
 */
export const CountryConfigSchema = z
  .object({
    code: z.string().openapi({
      description: 'ISO country code',
      example: 'US',
    }),
    name: z.string().openapi({
      description: 'Display name',
      example: 'United States',
    }),
    slug: z.string().openapi({
      description: 'URL slug',
      example: 'us',
    }),
    channelSlug: z.string().openapi({
      description: 'Saleor channel slug',
      example: 'us-usd',
    }),
    currency: z.string().openapi({
      description: 'ISO currency code',
      example: 'USD',
    }),
    defaultLocale: z.string().openapi({
      description: 'Default locale for country',
    }),
    locales: z.array(LocaleSchema).openapi({
      description: 'Available locales',
    }),
    continent: z.string().openapi({
      description: 'Continent name for grouping',
    }),
    favorite: z.boolean().optional().openapi({
      description: 'Show in favorites section',
    }),
  })
  .openapi('CountryConfig');

// =============================================================================
// Country Info Schemas (from Configuration App)
// =============================================================================

/**
 * Country information from Configuration App
 */
export const CountryInfoSchema = z
  .object({
    code: z.string().openapi({
      description: 'ISO country code',
      example: 'US',
    }),
    name: z.string().openapi({
      description: 'Country name',
      example: 'United States',
    }),
    region: z.string().openapi({
      description: 'Region',
      example: 'Americas',
    }),
    continent: z.string().openapi({
      description: 'Continent',
      example: 'North America',
    }),
    supported: z.boolean().openapi({
      description: 'Whether country is supported',
    }),
    favorite: z.boolean().openapi({
      description: 'Show in favorites',
    }),
    languages: z.array(z.string()).openapi({
      description: 'Supported languages',
      example: ['en-US', 'es-US'],
    }),
    channel: z.string().nullable().openapi({
      description: 'Saleor channel slug',
    }),
    currency: z.string().nullable().openapi({
      description: 'ISO currency code',
    }),
  })
  .openapi('CountryInfo');

/**
 * Language information from Configuration App
 */
export const LanguageInfoSchema = z
  .object({
    code: z.string().openapi({
      description: 'Language code',
      example: 'EN_US',
    }),
    contentful: z.string().openapi({
      description: 'Contentful locale code',
      example: 'en-US',
    }),
    locale: z.string().openapi({
      description: 'Internal locale code',
      example: 'en-US',
    }),
    name: z.string().openapi({
      description: 'Display name',
      example: 'English',
    }),
    slug: z.string().openapi({
      description: 'URL slug',
      example: 'en',
    }),
    sfCode: z.string().nullable().openapi({
      description: 'Salesforce language code',
    }),
  })
  .openapi('LanguageInfo');

/**
 * Supported country info with language details (from supportedCountriesInfo query)
 */
export const SupportedCountryInfoSchema = CountryInfoSchema.extend({
  languageInfos: z.array(LanguageInfoSchema).openapi({
    description: 'Detailed language information',
  }),
}).openapi('SupportedCountryInfo');

// =============================================================================
// Payment Limits Schemas
// =============================================================================

/**
 * Min/max payment limit
 */
export const PaymentLimitSchema = z
  .object({
    min: z.number().optional().openapi({
      description: 'Minimum payment amount',
    }),
    max: z.number().optional().openapi({
      description: 'Maximum payment amount',
    }),
  })
  .openapi('PaymentLimit');

/**
 * Payment limits for all methods
 */
export const PaymentLimitsSchema = z
  .object({
    affirm: PaymentLimitSchema.optional(),
    agos: PaymentLimitSchema.optional(),
    applepayExpress: PaymentLimitSchema.optional(),
    cembraFinancing: PaymentLimitSchema.optional(),
    cembraOpenInvoice: PaymentLimitSchema.optional(),
    creditPlus: PaymentLimitSchema.optional(),
    klarnaAccountFinancing: PaymentLimitSchema.optional(),
    klarnaPayLater: PaymentLimitSchema.optional(),
    klarnaPayNow: PaymentLimitSchema.optional(),
    oney3x: PaymentLimitSchema.optional(),
    oney4x: PaymentLimitSchema.optional(),
    oricoFinancing: PaymentLimitSchema.optional(),
    paypalExpress: PaymentLimitSchema.optional(),
    smbc: PaymentLimitSchema.optional(),
    twint: PaymentLimitSchema.optional(),
    v12: PaymentLimitSchema.optional(),
    yourAsset: PaymentLimitSchema.optional(),
    zip: PaymentLimitSchema.optional(),
  })
  .openapi('PaymentLimits');

// =============================================================================
// Checkout Config Schemas
// =============================================================================

/**
 * Checkout configuration for a country
 */
export const CheckoutConfigSchema = z
  .object({
    country: z.string().openapi({
      description: 'Country code',
    }),
    channel: z.string().nullable().openapi({
      description: 'Saleor channel slug',
    }),
    returnPeriodDays: z.number().nullable().openapi({
      description: 'Return/withdrawal window in days; 0 or null means no online returns',
    }),
    maxPayments: z.number().nullable().openapi({
      description: 'Maximum split payments allowed',
    }),
    firstPaymentMinPercentage: z.number().nullable().openapi({
      description: 'Minimum percentage for first payment',
    }),
    minOrderAmount: z.number().nullable().openapi({
      description: 'Minimum order amount',
    }),
    paymentLinkDuration: z.number().nullable().openapi({
      description: 'Payment link duration in hours',
    }),
    bankDetails: z.record(z.string(), z.unknown()).nullable().openapi({
      description: 'Bank transfer details',
    }),
    offlineBankTransferMinOrderAmount: z.number().nullable().openapi({
      description: 'Minimum order for bank transfer',
    }),
    offlineBankTransferWatchRequired: z.boolean().nullable().openapi({
      description: 'Whether watch must be held for bank transfer',
    }),
    paymentLimits: PaymentLimitsSchema.openapi({
      description: 'Payment method limits',
    }),
  })
  .openapi('CheckoutConfig');

// =============================================================================
// Module Activation Schemas
// =============================================================================

/**
 * Module activation state for a country
 */
export const ModuleActivationSchema = z
  .object({
    country: z.string().openapi({
      description: 'Country code',
    }),
    modules: z.record(z.string(), z.boolean()).openapi({
      description: 'Module activation states',
    }),
  })
  .openapi('ModuleActivation');

// =============================================================================
// Checkout Bundle Schemas
// =============================================================================

/**
 * Complete checkout bundle - all config needed for checkout
 */
export const CheckoutBundleSchema = z
  .object({
    countries: z.array(CountryInfoSchema).openapi({
      description: 'Available countries',
    }),
    checkoutConfig: CheckoutConfigSchema.nullable().openapi({
      description: 'Checkout configuration for current country',
    }),
    moduleActivation: ModuleActivationSchema.nullable().openapi({
      description: 'Module activation for current country',
    }),
    timestamp: z.number().openapi({
      description: 'Unix timestamp when data was fetched',
    }),
    source: z.enum(['api', 'fallback', 'fallback-error']).optional().openapi({
      description: 'Where the checkout config data came from (config availability)',
    }),
    ecommerceReady: z.boolean().optional().openapi({
      description: 'Whether the current market is e-commerce ready (config-driven gating)',
    }),
    ecommerceSource: z.enum(['api', 'fallback', 'legacy', 'brand']).optional().openapi({
      description:
        "Origin of the e-commerce readiness decision: 'api' (Market Config), 'fallback' (config outage, static list), 'legacy' (gating kill-switch off) or 'brand' (consultation-only brand, never ready)",
    }),
  })
  .openapi('CheckoutBundle');

// =============================================================================
// Derived TypeScript Types
// =============================================================================

/** Locale configuration */
export type Locale = z.infer<typeof LocaleSchema>;

/** Country configuration */
export type CountryConfig = z.infer<typeof CountryConfigSchema>;

/** Country information from Configuration App */
export type CountryInfo = z.infer<typeof CountryInfoSchema>;

/** Language information from Configuration App */
export type LanguageInfo = z.infer<typeof LanguageInfoSchema>;

/** Supported country info with language details */
export type SupportedCountryInfo = z.infer<typeof SupportedCountryInfoSchema>;

/** Payment limit (min/max) */
export type PaymentLimit = z.infer<typeof PaymentLimitSchema>;

/** Payment limits for all methods */
export type PaymentLimits = z.infer<typeof PaymentLimitsSchema>;

/** Checkout configuration */
export type CheckoutConfig = z.infer<typeof CheckoutConfigSchema>;

/** Module activation state */
export type ModuleActivation = z.infer<typeof ModuleActivationSchema>;

/** Complete checkout bundle */
export type CheckoutBundle = z.infer<typeof CheckoutBundleSchema>;
